import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { FormSubmitButton } from '@/components/ui/FormSubmitButton'
import { useSystems } from '@/hooks/use-locations'
import { useRoleSync } from '@/hooks/use-role-sync'
import {
  type CreateUserFormData,
  type UpdateUserFormData,
  createUserSchema,
  updateUserSchema,
  type UserFormInput,
} from '@/lib/validation/user-schemas'

import { AccountStatusSection } from './form-sections/AccountStatusSection'
import { BasicInfoSection } from './form-sections/BasicInfoSection'
import { ClientRolesSection } from './form-sections/ClientRolesSection'
import { LegacySectionsSection } from './form-sections/LegacySectionsSection'
import { PortalSectionsSection } from './form-sections/PortalSectionsSection'
import { RetailLocationsAccessSection } from './form-sections/RetailLocationsAccessSection'
import { RolePermissionsSection } from './form-sections/RolePermissionsSection'

interface BaseUserFormProps {
  isLoading?: boolean
  error?: string | null
  canWrite?: boolean
}

interface CreateUserFormProps extends BaseUserFormProps {
  mode: 'create'
  defaultValues?: Partial<CreateUserFormData>
  onSubmit: (data: CreateUserFormData) => Promise<void>
}

interface EditUserFormProps extends BaseUserFormProps {
  mode: 'edit'
  defaultValues?: Partial<UpdateUserFormData>
  onSubmit: (data: UpdateUserFormData) => Promise<void>
}

type UserFormProps = CreateUserFormProps | EditUserFormProps

const defaultFormValues = {
  basicInfo: {
    email: '',
    username: '',
    firstName: '',
    lastName: '',
    password: '',
    temporaryPassword: true,
  },
  accountStatus: {
    enabled: true,
    emailVerified: false,
    locked: false,
  },
  realmRoles: {
    cs_sys_op: false,
    cs_sys_admin: false,
    cs_active: true,
    cs_locked: false,
    cs_systems_all: false,
    cs_locations_all: false,
    cs_sections_all: false,
  },
  clientRoles: {},
  attributes: {
    systems: [],
    systems_all: false,
    dbas: [],
    districts: [],
    locations: [],
    locations_all: false,
    sections: [],
    sections_all: false,
  },
}

export function UserForm({
  mode,
  defaultValues,
  onSubmit,
  isLoading = false,
  error,
  canWrite = true,
}: UserFormProps) {
  const navigate = useNavigate()
  const isEditMode = mode === 'edit'

  const { data: systems, isLoading: isLoadingSystems } = useSystems()

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>({
    resolver: zodResolver(isEditMode ? updateUserSchema : createUserSchema),
    defaultValues: defaultValues ?? defaultFormValues,
  })

  useRoleSync({ watch, setValue })

  const handleFormSubmit = async (data: CreateUserFormData | UpdateUserFormData) => {
    if (isEditMode) {
      await (onSubmit as (data: UpdateUserFormData) => Promise<void>)(data as UpdateUserFormData)
      return
    }

    await (onSubmit as (data: CreateUserFormData) => Promise<void>)(data as CreateUserFormData)
  }

  const handleCancel = () => {
    navigate('/operations/users')
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <BasicInfoSection
        register={register}
        setValue={setValue}
        watch={watch}
        errors={errors}
        isEditMode={isEditMode}
      />
      <AccountStatusSection register={register} errors={errors} />
      <RolePermissionsSection
        register={register}
        setValue={setValue}
        watch={watch}
        errors={errors}
      />
      <ClientRolesSection register={register} setValue={setValue} watch={watch} errors={errors} />
      <PortalSectionsSection control={control} errors={errors} watch={watch} />
      <LegacySectionsSection control={control} watch={watch} />
      <RetailLocationsAccessSection
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        systems={systems}
        isLoadingData={isLoadingSystems}
      />

      <FormAlert
        type="error"
        message={error}
        title={isEditMode ? 'Error updating user' : 'Error creating user'}
      />

      <FormActions
        onCancel={handleCancel}
        isLoading={isSubmitting || isLoading}
        canWrite={canWrite}
        submitLabel={isEditMode ? 'Update User' : 'Create User'}
      />
    </form>
  )
}

const FormAlert = ({
  type,
  message,
  title,
}: {
  type: 'error' | 'success'
  message: string | null | undefined
  title: string
}) => {
  if (!message) return null

  const isError = type === 'error'
  const bgClass = isError ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'
  const borderClass = isError
    ? 'border-red-200 dark:border-red-800'
    : 'border-green-200 dark:border-green-800'
  const iconColor = isError
    ? 'text-red-600 dark:text-red-400'
    : 'text-green-600 dark:text-green-400'
  const titleColor = isError
    ? 'text-red-800 dark:text-red-200'
    : 'text-green-800 dark:text-green-200'
  const textColor = isError
    ? 'text-red-700 dark:text-red-300'
    : 'text-green-700 dark:text-green-300'
  const icon = isError ? '\u2715' : '\u2713'

  return (
    <div className={`p-4 ${bgClass} border ${borderClass} rounded-lg`}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-5 h-5 ${iconColor}`}>{icon}</div>
        <div className="flex-1">
          <h3 className={`text-sm font-medium ${titleColor}`}>{title}</h3>
          <p className={`mt-1 text-sm ${textColor}`}>{message}</p>
        </div>
      </div>
    </div>
  )
}

const FormActions = ({
  onCancel,
  isLoading,
  canWrite,
  submitLabel,
}: {
  onCancel: () => void
  isLoading: boolean
  canWrite: boolean
  submitLabel: string
}) => (
  <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
    <button
      type="button"
      onClick={onCancel}
      disabled={isLoading}
      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Cancel
    </button>
    <FormSubmitButton
      isLoading={isLoading}
      isValid
      canWrite={canWrite}
      writeGateResource="User Management"
    >
      {submitLabel}
    </FormSubmitButton>
  </div>
)
